import type { ClientApiApi as AppClient, User } from "@autointel/client-api";
import { AiSdkInterface, AuthEnvironment } from "@autointel/whiz-models";
import createCache from "@emotion/cache";
import InteractionsListener from "./InteractionsListener.ts";
import BmwRs from "./tenant/bmw-rs.ts";
import { UIConfiguration } from "./types/UIConfiguration.ts";
import type { AbstractWhiz, WhizVideo } from "./whiz";
import type { AbstractInventory } from "./inventory";

// eslint-disable-next-line import/no-webpack-loader-syntax
// @ts-ignore
import css from '!!css-loader?{"sourceMap":false,"exportType":"string"}!./ui/DefaultCssVariables.css';

export abstract class AbstractAiSdk implements AiSdkInterface {
    public app?: AppClient;
    public whiz?: AbstractWhiz;
    public whizVideo?: WhizVideo;
    public inventory?: AbstractInventory;
    public user?: User;
    public interactionsListener = new InteractionsListener();

    private authEnvironment: AuthEnvironment = AuthEnvironment.PROD;
    private stagingEnabled = false;

    #pageListenerEnabled = false;
    #uiLoading = false;
    #uiEnabled = false;

    async setCredentials(tenant: string, username: string, apiKey: string, whizSecret?: string) {
        const { ClientApiApi } = await import("@autointel/client-api");
        this.app = new ClientApiApi();
        this.app.useTenant(tenant);
        this.app.useHmacCredentials(username, apiKey);

        if (this.stagingEnabled) {
            this.app.enableStaging();
        } else {
            this.app.disableStaging();
        }

        this.interactionsListener.app = this.app;

        const { Inventory } = await import("./inventory");
        this.inventory = new Inventory();
        this.inventory.setTenant(tenant);
        this.inventory.useHmacCredentials(username, apiKey);
        this.inventory.setAuthEnvironment(this.authEnvironment);
        
        if (this.stagingEnabled) {
            this.inventory.enableStaging();
        } else {
            this.inventory.disableStaging();
        }

        if (!!whizSecret) {
            const { Whiz } = await import("./whiz");
            this.whiz = new Whiz();

            const { WhizVideo } = await import("./whiz/video");
            this.whizVideo = new WhizVideo();

            this.whiz.setTenant(tenant);
            this.whizVideo.setTenant(tenant);

            this.whiz.useHmacCredentials(username, apiKey);
            this.whizVideo.useHmacCredentials(username, apiKey);

            this.whiz.setSecret(whizSecret);
            this.whizVideo.setSecret(whizSecret);

            this.whiz.setAuthEnvironment(this.authEnvironment);
            this.whizVideo.setAuthEnvironment(this.authEnvironment);

            if (this.stagingEnabled) {
                this.whiz.enableStaging();
                this.whizVideo.enableStaging();
            } else {
                this.whiz.disableStaging();
                this.whizVideo.disableStaging();
            }
        }
    }

    getTenant() {
        return this.app?.getCredentials().tenant;
    }

    abstract setUser(user: User & { auto?: boolean }): Promise<User | null>;

    getUserProfile() {
        return this.user;
    }

    setAuthEnvironment(authEnvironment: AuthEnvironment) {
        this.authEnvironment = authEnvironment;

        if (!this.whiz || !this.whizVideo) {
            console.warn("Credentials not found for whizSecret");
            return;
        }

        this.whiz.setAuthEnvironment(authEnvironment);
        this.whizVideo.setAuthEnvironment(authEnvironment);
        this.inventory?.setAuthEnvironment(authEnvironment);
    }

    enableStaging() {
        this.stagingEnabled = true;
        this.app?.enableStaging();
        this.whiz?.enableStaging();
        this.whizVideo?.enableStaging();
        this.inventory?.enableStaging();
    }

    disableStaging() {
        this.stagingEnabled = false;
        this.app?.disableStaging();
        this.whiz?.disableStaging();
        this.whizVideo?.disableStaging();
        this.inventory?.disableStaging();
    }

    debugLog(...args: any[]) {
        if (this.authEnvironment !== AuthEnvironment.PROD) {
            console.log(args.join(""));
        }
    }

    async enableUi(
        selector: string | null,
        {
            logoUrl,
            title,
            avatarUrl,
            button,
            language,
            openDirection = "top-left",
            xOffset = 0,
            yOffset = 0,
            disableTenantCustomization = false,
            defaultOpen = false,
        }: UIConfiguration,
    ) {
        if (this.#uiLoading || this.#uiEnabled) {
            console.warn("Whiz UI is already enabled!");
            return;
        }

        this.#uiLoading = true;

        const { createElement } = await import("react");
        const { createRoot } = await import("react-dom/client");
        const App = (await import("./ui/App")).default;
        const { getTheme } = await import("./ui/theme");

        const metadata = await this.whiz?.getAndSetClientMetadata();

        if (!selector) {
            const whizContainer = document.createElement("div");
            whizContainer.setAttribute("id", "whiz-chat");
            whizContainer.style.position = "fixed";
            whizContainer.style.bottom = `${
                20 +
                yOffset +
                (metadata?.chatContainerYOffset ?? 0) +
                (button?.enabled ?? metadata?.button?.enabled ? (metadata?.button?.size ?? 56) + 16 : 0)
            }px`;
            whizContainer.style.right = `${20 + xOffset + (metadata?.chatContainerXOffset ?? 0)}px`;
            whizContainer.style.zIndex = "2147483646";

            const whizChatRoot = whizContainer.attachShadow({ mode: "closed" });
            whizChatRoot.innerHTML = `<style>${css}</style>`;
            const cssCache = createCache({
                key: "css",
                prepend: true,
                container: whizChatRoot,
            });
            const theme = getTheme(whizChatRoot);

            document.body.appendChild(whizContainer);

            const reactRoot = createRoot(whizChatRoot);
            const appElement = createElement(
                App,
                {
                    title: metadata?.overrideTitle ? metadata?.title : title ?? metadata?.title ?? "Chatbot",
                    avatarUrl: metadata?.overrideAvatarUrl
                        ? metadata?.chatAvatarUrl
                        : avatarUrl ?? metadata?.chatAvatarUrl,
                    logoUrl: metadata?.overrideLogoUrl ? metadata?.chatLogoUrl : logoUrl ?? metadata?.chatLogoUrl,
                    language: metadata?.overrideLanguage ? metadata?.language : language ?? metadata?.language,
                    cssCache,
                    theme,
                    button,
                    aiSdk: this,
                    defaultOpen,
                },
                null,
            );
            reactRoot.render(appElement);
            this.#uiEnabled = true;
            this.#uiLoading = false;
            return;
        }

        let buttonElement: HTMLElement | null = null;
        if (!disableTenantCustomization) {
            switch (this.getTenant()) {
                case "bmw-rs":
                    buttonElement = BmwRs.getButtonElement();
                    break;

                default:
                    buttonElement = document.getElementById(selector);
                    break;
            }
        } else {
            buttonElement = document.getElementById(selector);
        }

        if (buttonElement && buttonElement.parentElement) {
            const whizContainer = document.createElement("div");
            whizContainer.setAttribute("id", "whiz-chat");
            whizContainer.style.position = "fixed";
            whizContainer.style.zIndex = "9999";
            switch (openDirection) {
                case "bottom-right":
                    whizContainer.style.top = `calc(${buttonElement.offsetHeight}px + 1em + ${yOffset}px)`;
                    whizContainer.style.left = `calc(0px + ${xOffset}px)`;
                    break;

                case "bottom-left":
                    whizContainer.style.top = `calc(${buttonElement.offsetHeight}px + 1em + ${yOffset}px)`;
                    whizContainer.style.right = `calc(0px + ${xOffset}px)`;
                    break;

                case "top-right":
                    whizContainer.style.bottom = `calc(${buttonElement.offsetHeight}px + 1em + ${yOffset}px)`;
                    whizContainer.style.left = `calc(0px + ${xOffset}px)`;
                    break;

                case "top-left":
                default:
                    whizContainer.style.bottom = `calc(${buttonElement.offsetHeight}px + 1em + ${yOffset}px)`;
                    whizContainer.style.right = `calc(0px + ${xOffset}px)`;
            }

            const whizChatRoot = whizContainer.attachShadow({ mode: "closed" });
            whizChatRoot.innerHTML = `<style>${css}</style>`;
            const cssCache = createCache({
                key: "css",
                prepend: true,
                container: whizChatRoot,
            });
            const theme = getTheme(whizChatRoot);

            (buttonElement.parentElement ?? document.body).appendChild(whizContainer);
            const reactRoot = createRoot(whizChatRoot);
            const appElement = createElement(
                App,
                {
                    title: metadata?.overrideTitle ? metadata?.title : title ?? metadata?.title ?? "Chatbot",
                    avatarUrl: metadata?.overrideAvatarUrl
                        ? metadata?.chatAvatarUrl
                        : avatarUrl ?? metadata?.chatAvatarUrl,
                    logoUrl: metadata?.overrideLogoUrl ? metadata?.chatLogoUrl : logoUrl ?? metadata?.chatLogoUrl,
                    language: metadata?.overrideLanguage ? metadata?.language : language ?? metadata?.language,
                    cssCache,
                    theme,
                    aiSdk: this,
                    button,
                    buttonElement,
                    defaultOpen,
                },
                null,
            );
            reactRoot.render(appElement);
            this.#uiEnabled = true;
            this.#uiLoading = false;
        } else {
            console.error(`Could not find #${selector}!`);
        }
    }
}
